import type { InvoiceDetailResponse, InvoiceType, NexDetailLine } from '../../../types/invoice';
import type { NexReconciliationIdentifierMode } from '../../../utils/invoiceNexIdentifierUi';
import { isCreditCardMiscFeeLine } from '../../../utils/invoicePaySettlement';
import { computeNexSubmitApDistPreview } from '../../../utils/nexSubmitManualPo';
import { resolveDefaultNexFolderNo } from '../../../utils/nexFolderResolution';
import { resolveNexSubmitReference, resolveNexRoContactId } from '../../../utils/resolveNexSubmitReference';

export type NexSubmitComputeArgs = Omit<
    Parameters<typeof computeNexSubmitApDistPreview>[0],
    'manualPoHeaderOverrides'
>;

export interface BuildNexSubmitComputeArgsInput {
    invoice: InvoiceDetailResponse;
    editedFields: Partial<InvoiceDetailResponse>;
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
    recon: {
        unmatchedPartIndices?: Set<number>;
        localMatchedNexOverrides?: Record<number, NexDetailLine>;
    } | null | undefined;
    preservedMatchedNexLines: Array<{ partIndex: number; nexLine: NexDetailLine }>;
    remapSubmitPreviewPartIndex: (idx: number) => number | null;
    salesTaxAsLineItem?: boolean;
}

export function buildNexSubmitComputeArgs({
    invoice,
    editedFields,
    nexReconciliationIdentifierMode = 'ro',
    recon,
    preservedMatchedNexLines,
    remapSubmitPreviewPartIndex,
    salesTaxAsLineItem = false,
}: BuildNexSubmitComputeArgsInput): NexSubmitComputeArgs | null {
    const identifierInput = {
        mode: nexReconciliationIdentifierMode,
        roNumber: editedFields.roNumber ?? invoice.roNumber,
        purchaseOrderNumber: editedFields.purchaseOrderNumber ?? invoice.purchaseOrderNumber,
        shopRoNumber: editedFields.shopRoNumber ?? invoice.shopRoNumber,
    };
    const hasIdentifier = Boolean(resolveNexSubmitReference(identifierInput));
    if (!hasIdentifier) return null;

    const roContactId = resolveNexRoContactId(identifierInput);
    const roForFolder = roContactId || resolveNexSubmitReference(identifierInput);

    const lineItems = editedFields.lineItems ?? invoice.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice.freightItems ?? [];
    const fuelItems = editedFields.fuelItems ?? invoice.fuelItems ?? [];
    const restockingRaw = editedFields.restockingFeeItems ?? invoice.restockingFeeItems ?? [];
    const restockingFeeItems =
        (editedFields.paySettlementType ?? invoice.paySettlementType) === 'charge'
            ? restockingRaw.filter((p) => !isCreditCardMiscFeeLine(p))
            : restockingRaw;

    const nexResponse = (invoice.nexResponse ?? null) as NexSubmitComputeArgs['nexResponse'];
    if (resolveDefaultNexFolderNo(nexResponse, roForFolder) == null) return null;

    const unmatchedSet = new Set(Array.from(recon?.unmatchedPartIndices ?? []));
    const manualMatchedPartIndexSet = new Set(
        Object.keys(recon?.localMatchedNexOverrides ?? {})
            .map((idxStr) => parseInt(idxStr, 10))
            .filter((idx) => !Number.isNaN(idx)),
    );
    const flaggedAsMissingOrDifferentPartIndexSet = new Set(
        (invoice.reconciliationIssues?.flaggedParts ?? [])
            .filter(
                (issue: { partIndex?: number; issueType?: string }) =>
                    (issue?.partIndex ?? -1) >= 0 &&
                    (issue?.issueType === 'missing_in_nex' ||
                        issue?.issueType === 'different_part_names' ||
                        issue?.issueType === 'different_positions'),
            )
            .map((issue: { partIndex: number }) => issue.partIndex)
            .map((idx: number) => remapSubmitPreviewPartIndex(idx))
            .filter((idx): idx is number => idx != null),
    );

    const localOverrideIndices = new Set(
        Object.keys(recon?.localMatchedNexOverrides ?? {})
            .map((s) => parseInt(s, 10))
            .filter((n) => !Number.isNaN(n)),
    );
    const baseMatchedFromInvoice = invoice.reconciliationIssues?.matchedNexLines ?? [];
    const baseMatched =
        baseMatchedFromInvoice.length > 0 ? baseMatchedFromInvoice : preservedMatchedNexLines;

    const matchedNexLines: Array<{ partIndex: number; nexLine: { poHeaderID?: number; lineNumber?: number } }> = [];
    const seenPartIndex = new Set<number>();
    for (const m of baseMatched) {
        const nextIdx = remapSubmitPreviewPartIndex(m.partIndex);
        if (nextIdx == null || Number.isNaN(nextIdx)) continue;
        if (unmatchedSet.has(nextIdx) || localOverrideIndices.has(nextIdx)) continue;
        if (seenPartIndex.has(nextIdx)) continue;
        seenPartIndex.add(nextIdx);
        matchedNexLines.push({
            partIndex: nextIdx,
            nexLine: {
                poHeaderID: m.nexLine?.poHeaderID,
                lineNumber: m.nexLine?.lineNumber,
            },
        });
    }
    for (const [idxStr, nexLine] of Object.entries(recon?.localMatchedNexOverrides ?? {})) {
        const idx = parseInt(idxStr, 10);
        if (Number.isNaN(idx) || unmatchedSet.has(idx)) continue;
        if (seenPartIndex.has(idx)) continue;
        seenPartIndex.add(idx);
        matchedNexLines.push({
            partIndex: idx,
            nexLine: {
                poHeaderID: nexLine?.poHeaderID,
                lineNumber: nexLine?.lineNumber,
            },
        });
    }

    const invoiceType = (editedFields.invoiceType ?? invoice.invoiceType ?? 'received') as InvoiceType;
    const taxesTotal =
        typeof editedFields.taxesTotal === 'number'
            ? editedFields.taxesTotal
            : typeof invoice.taxesTotal === 'number'
              ? invoice.taxesTotal
              : 0;
    const nexTaxFormat = (editedFields.nexTaxFormat ?? invoice.nexTaxFormat) || 'single';

    return {
        roNumber: roForFolder,
        invoiceType,
        lineItems,
        freightItems,
        fuelItems,
        restockingFeeItems,
        nexResponse,
        matchedNexLines,
        unmatchedPartIndices: Array.from(unmatchedSet),
        manualMatchedPartIndices: Array.from(manualMatchedPartIndexSet),
        flaggedAsMissingOrDifferentPartIndices: Array.from(flaggedAsMissingOrDifferentPartIndexSet),
        invoiceSupplierText: editedFields.supplierText ?? invoice.supplierText,
        salesTaxAsLineItem,
        taxesTotal,
        nexTaxFormat,
    };
}
